import React, { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  TextInput,
  StyleSheet,
} from 'react-native';
import { useLanguage } from '../contexts/LanguageContext';

export default function SearchableMultiSelect({
  label,
  options,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  options: { id: string; name: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const all = [
    ...options,
    ...value
      .filter((id) => !options.some((o) => o.id === id))
      .map((id) => ({ id, name: id })),
  ];
  const selected = all.filter((o) => value.includes(o.id));
  return (
    <View style={s.container}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        disabled={disabled}
        onPress={() => setOpen(!open)}
        style={s.header}
      >
        <Text style={s.label}>
          {label} ({value.length}) {open ? '▴' : '▾'}
        </Text>
      </Pressable>
      {!!selected.length && (
        <Text style={s.summary}>{selected.map((o) => o.name).join(', ')}</Text>
      )}
      {open && (
        <>
          <TextInput
            accessibilityLabel={t('operations.searchCaretakers')}
            placeholder={t('operations.searchCaretakers')}
            value={search}
            onChangeText={setSearch}
            style={s.search}
          />
          <ScrollView
            nestedScrollEnabled
            style={s.list}
            keyboardShouldPersistTaps="handled"
          >
            {all
              .filter((o) =>
                o.name
                  .toLocaleLowerCase()
                  .includes(search.toLocaleLowerCase().trim()),
              )
              .map((option) => (
                <Pressable
                  key={option.id}
                  accessibilityRole="checkbox"
                  accessibilityLabel={option.name}
                  accessibilityState={{ checked: value.includes(option.id) }}
                  disabled={disabled}
                  onPress={() =>
                    onChange(
                      value.includes(option.id)
                        ? value.filter((id) => id !== option.id)
                        : [...value, option.id],
                    )
                  }
                  style={s.row}
                >
                  <Text style={s.check}>
                    {value.includes(option.id) ? '☑' : '☐'}
                  </Text>
                  <Text style={s.name}>{option.name}</Text>
                </Pressable>
              ))}
          </ScrollView>
        </>
      )}
    </View>
  );
}
const s = StyleSheet.create({
  container: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
  },
  header: { padding: 10 },
  label: { fontWeight: '600', color: '#334155' },
  summary: {
    fontSize: 12,
    color: '#64748B',
    paddingHorizontal: 10,
    paddingBottom: 8,
  },
  search: {
    margin: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 6,
  },
  list: { maxHeight: 180 },
  row: { flexDirection: 'row', alignItems: 'center', padding: 10, gap: 10 },
  check: { fontSize: 20, color: '#2563EB' },
  name: { flex: 1, color: '#334155' },
});
